import type { CsticReference, VisibilityCondition } from "../../contract/definition";
import type { ConfiguratorInstanceContext } from "../../contract/configuration";

const getReferencedValue = (reference: CsticReference, context: ConfiguratorInstanceContext): string | null => {
  const instance = reference.scope === "main" ? context.mainInstance : context.instance;
  return instance.cstics[reference.name]?.value ?? null;
};

export const evaluateCondition = (
  condition: VisibilityCondition | undefined,
  context: ConfiguratorInstanceContext,
): boolean => {
  if (!condition) {
    return true;
  }

  if ("valueIn" in condition) {
    return condition.valueIn.includes(getReferencedValue(condition.cstic, context) ?? "");
  }

  if ("numeric" in condition) {
    const rawValue = getReferencedValue(condition.cstic, context);
    if (rawValue === null || rawValue.trim() === "") {
      return false;
    }

    const value = Number(rawValue);
    if (!Number.isFinite(value)) {
      return false;
    }

    switch (condition.numeric.operator) {
      case "lt":
        return value < condition.numeric.value;
      case "lte":
        return value <= condition.numeric.value;
      case "gt":
        return value > condition.numeric.value;
      case "gte":
        return value >= condition.numeric.value;
    }

    return false;
  }

  if ("hasValue" in condition) {
    const value = getReferencedValue(condition.hasValue, context);
    return value !== null && value !== "";
  }

  if ("all" in condition) {
    return condition.all.every((child) => evaluateCondition(child, context));
  }

  if ("any" in condition) {
    return condition.any.some((child) => evaluateCondition(child, context));
  }

  return !evaluateCondition(condition.not, context);
};

export const visitConditionCstics = (
  condition: VisibilityCondition | undefined,
  visitor: (reference: CsticReference) => void,
) => {
  if (!condition) {
    return;
  }

  if ("cstic" in condition) {
    visitor(condition.cstic);
    return;
  }

  if ("hasValue" in condition) {
    visitor(condition.hasValue);
    return;
  }

  if ("all" in condition) {
    condition.all.forEach((child) => visitConditionCstics(child, visitor));
    return;
  }

  if ("any" in condition) {
    condition.any.forEach((child) => visitConditionCstics(child, visitor));
    return;
  }

  visitConditionCstics(condition.not, visitor);
};
